<template>
    <jet-form-section @submitted="updateProfileInformation">
        <template #title>
            Profile Information
        </template>

        <template #description>
            Update your account's profile information and email address.
            <alert-card type="warning" v-if="$page.props.user.role === 'agent' && $page.props.user.required_steps?.length && (($page.props.user.completed_steps?.length ?? 0) / $page.props.user.required_steps.length * 100) < 100">
                Your profile is <strong>{{ (($page.props.user.completed_steps?.length ?? 0) / $page.props.user.required_steps.length) * 100 }}%</strong> complete.
                <span v-if="(($page.props.user.completed_steps?.length ?? 0) / $page.props.user.required_steps.length * 100) < 100"> Please complete your profile to get the most out of RentRisk.</span>
                <ul class="mt-4" v-if="(($page.props.user.completed_steps?.length ?? 0) / $page.props.user.required_steps.length * 100) < 100">
                    <li v-for="step in $page.props.user.required_steps" :key="step">
                        <span v-if="!$page.props.user.completed_steps?.includes(step)">
                            <font-awesome-icon :icon="faCircleX" class="text-red-700" />
                        </span>
                        <span v-else>
                            <font-awesome-icon :icon="faCheckCircle" class="text-green-700" />
                        </span>
                        - {{ step.replaceAll('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) }}
                    </li>
                </ul>
            </alert-card>            
        </template>

        <template #form>
            <!-- Profile Photo -->
            <div class="col-span-6 sm:col-span-4" v-if="$page.props.jetstream.managesProfilePhotos">
                <!-- Profile Photo File Input -->
                <input type="file" class="hidden"
                            ref="photo"
                            @change="updatePhotoPreview">

                <jet-label for="photo" value="Photo" />

                <!-- Current Profile Photo -->
                <div class="mt-2" v-show="! photoPreview">
                    <img :src="user.profile_photo_url" :alt="user.name" class="rounded-full h-20 w-20 object-cover">
                </div>

                <!-- New Profile Photo Preview -->
                <div class="mt-2" v-show="photoPreview">
                    <span class="block rounded-full w-20 h-20"
                          :style="'background-size: cover; background-repeat: no-repeat; background-position: center center; background-image: url(\'' + photoPreview + '\');'">
                    </span>
                </div>

                <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="selectNewPhoto">
                    Select A New Photo
                </jet-secondary-button>

                <jet-secondary-button type="button" class="mt-2" @click.prevent="deletePhoto" v-if="user.profile_photo_path">
                    Remove Photo
                </jet-secondary-button>

                <jet-input-error :message="form.errors.photo" class="mt-2" />
            </div>

            <!-- Name -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="name" value="Name" />
                <jet-input id="name" type="text" class="mt-1 block w-full" v-model="form.name" autocomplete="name" />
                <jet-input-error :message="form.errors.name" class="mt-2" />
            </div>

            <!-- Email -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="email" value="Email" />
                <jet-input id="email" type="email" class="mt-1 block w-full" v-model="form.email" />
                <jet-input-error :message="form.errors.email" class="mt-2" />
            </div>

            <!-- Home Phone -->
            <div v-if="$page.props.user.role === 'agent'" class="col-span-6 sm:col-span-4">
                <jet-label for="home_phone" value="Home Phone" />
                <jet-input id="home_phone" type="text" class="mt-1 block w-full" v-model="form.home_phone" />
                <jet-input-error :message="form.errors.home_phone" class="mt-2" />
            </div>

            <!-- Cell Phone -->
            <div v-if="$page.props.user.role === 'agent'" class="col-span-6 sm:col-span-4">
                <jet-label for="cell_phone" value="Cell Phone" />
                <jet-input id="cell_phone" type="text" class="mt-1 block w-full" v-model="form.cell_phone" />
                <jet-input-error :message="form.errors.cell_phone" class="mt-2" />
            </div>

            <!-- Office Phone -->
            <div v-if="$page.props.user.role === 'agent'" class="col-span-6 sm:col-span-4">
                <jet-label for="office_phone" value="Office Phone" />
                <jet-input id="office_phone" type="text" class="mt-1 block w-full" v-model="form.office_phone" />
                <jet-input-error :message="form.errors.office_phone" class="mt-2" />
            </div>

            <!-- Company name -->
            <div v-if="$page.props.user.role === 'agent'" class="col-span-6 sm:col-span-4">
                <jet-label for="company_name" value="Company Name" />
                <template v-if="registeredCompanyName">
                    <p id="company_name" class="mt-1 block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {{ registeredCompanyName }}
                    </p>
                    <p class="mt-1 text-sm text-gray-500">
                        This is the name your company is registered under with Experian, and the name applicants
                        see when they share a report with you. It cannot be edited here.
                    </p>
                </template>
                <template v-else>
                    <jet-input id="company_name" type="text" class="mt-1 block w-full" v-model="form.company_name" />
                    <jet-input-error :message="form.errors.company_name" class="mt-2" />
                </template>
            </div>

            <!-- Company website -->
            <div v-if="$page.props.user.role === 'agent'" class="col-span-6 sm:col-span-4">
                <jet-label for="company_website" value="Company Website" />
                <jet-input id="company_website" type="text" class="mt-1 block w-full" v-model="form.company_website" />
                <jet-input-error :message="form.errors.company_website" class="mt-2" />
            </div>

            <!-- Facebook url -->
            <div v-if="$page.props.user.role === 'agent'" class="col-span-6 sm:col-span-4">
                <jet-label for="facebook_url" value="Facebook URL" />
                <jet-input id="facebook_url" type="text" class="mt-1 block w-full" v-model="form.facebook_url" />
                <jet-input-error :message="form.errors.facebook_url" class="mt-2" />
            </div>

            <!-- Instagram url -->
            <div v-if="$page.props.user.role === 'agent'" class="col-span-6 sm:col-span-4">
                <jet-label for="instagram_url" value="Instagram URL" />
                <jet-input id="instagram_url" type="text" class="mt-1 block w-full" v-model="form.instagram_url" />
                <jet-input-error :message="form.errors.instagram_url" class="mt-2" />
            </div>

            <!-- Linkedin url -->
            <div v-if="$page.props.user.role === 'agent'" class="col-span-6 sm:col-span-4">
                <jet-label for="linkedin_url" value="Linkedin URL" />
                <jet-input id="linkedin_url" type="text" class="mt-1 block w-full" v-model="form.linkedin_url" />
                <jet-input-error :message="form.errors.linkedin_url" class="mt-2" />
            </div>

        </template>

        <template #actions>
            <jet-action-message :on="form.recentlySuccessful" class="mr-3">
                Saved.
            </jet-action-message>

            <jet-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                Save
            </jet-button>
        </template>
    </jet-form-section>
</template>

<script>
    import JetButton from '@/Jetstream/Button.vue'
    import JetFormSection from '@/Jetstream/FormSection.vue'
    import JetInput from '@/Jetstream/Input.vue'
    import JetInputError from '@/Jetstream/InputError.vue'
    import JetLabel from '@/Jetstream/Label.vue'
    import JetActionMessage from '@/Jetstream/ActionMessage.vue'
    import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue'
    import { router } from '@inertiajs/vue3';
    import AlertCard from "@/Shared/AlertCard.vue";
    import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
    import { faCheckCircle, faCircleX } from "@fortawesome/pro-solid-svg-icons";

    export default {
        components: {
            JetActionMessage,
            JetButton,
            JetFormSection,
            JetInput,
            JetInputError,
            JetLabel,
            JetSecondaryButton,
            AlertCard,
            FontAwesomeIcon
        },

        props: ['user'],

        data() {
            return {
                form: this.$inertia.form({
                    _method: 'PUT',
                    name: this.user.name,
                    email: this.user.email,
                    home_phone: this.user.home_phone,
                    cell_phone: this.user.cell_phone,
                    office_phone: this.user.office_phone,
                    company_name: this.user.company_name,
                    company_website: this.user.company_website,
                    photo: null,
                    facebook_url: this.user.facebook_url,
                    instagram_url: this.user.instagram_url,
                    linkedin_url: this.user.linkedin_url
                }),

                photoPreview: null,
                faCheckCircle,
                faCircleX
            }
        },

        computed: {
            /**
             * The legal entity name the company is registered under with
             * Experian, when one resolves. It is what applicants see on the
             * share page, so it is shown here read-only rather than alongside
             * a free-text field that says something different.
             *
             * Read from auth.user, NOT the top-level user prop. They are two
             * different shares: `user` is Jetstream's raw $user->toArray(), so
             * it carries only real model attributes — which is why the sibling
             * `$page.props.user.role` works and this would not. Anything
             * HandleInertiaRequests derives lives under auth.user.
             */
            registeredCompanyName() {
                return this.$page.props.auth?.user?.enterprise_company_name || null
            }
        },

        methods: {
            updateProfileInformation() {
                if (this.$refs.photo) {
                    this.form.photo = this.$refs.photo.files[0]
                }

                this.form.post(route('user-profile-information.update'), {
                    errorBag: 'updateProfileInformation',
                    preserveScroll: true,
                    onSuccess: () => {
                        this.clearPhotoFileInput();

                        // Reload page data to get updated progress
                        router.reload({ 
                            only: ['progress'], // Only reload specific props if needed
                            preserveScroll: true 
                        });
                    }
                });
            },

            selectNewPhoto() {
                this.$refs.photo.click();
            },

            updatePhotoPreview() {
                const photo = this.$refs.photo.files[0];

                if (! photo) return;

                const reader = new FileReader();

                reader.onload = (e) => {
                    this.photoPreview = e.target.result;
                };

                reader.readAsDataURL(photo);
            },

            deletePhoto() {
                this.$inertia.delete(route('current-user-photo.destroy'), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.photoPreview = null;
                        this.clearPhotoFileInput();

                        // Reload page data to get updated progress
                        router.reload({ 
                            only: ['progress'], // Only reload specific props if needed
                            preserveScroll: true 
                        });
                    },
                });
            },

            clearPhotoFileInput() {
                if (this.$refs.photo?.value) {
                    this.$refs.photo.value = null;
                }
            },
        },
    }
</script>
